import React, { useState, useCallback, useMemo, useEffect } from "react"
import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import { Form, FormFeedback, Label, Button, Row, Col, Table } from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import {
  useCreateAccessoriesList,
  useEditAccessoriesList,
} from "hooks/useAccessoriesLists"
import { useGetCompaniesList } from "hooks/useInventory"
import CompanyStaffSelector from "./components/CompanyStaffSelector"

const InventoryAddEdit = ({ modal, toggleModal, isEdit, editedData }) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateAccessoriesList()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditAccessoriesList()


  // Accessories list from the PDF
  const accessoriesList = [
    { id: 1, name: "Key/Remote", remarks: "" },
    { id: 2, name: "Front Bumper", remarks: "" },
    { id: 3, name: "Rear Bumper", remarks: "" },
    { id: 4, name: "Bonnet / Hood", remarks: "" },
    { id: 5, name: "Roof Condition", remarks: "" },
    { id: 6, name: "Front Left Door", remarks: "" },
    { id: 7, name: "Front Right Door", remarks: "" },
    { id: 8, name: "Rear Left Door", remarks: "" },
    { id: 9, name: "Rear Right Door", remarks: "" },
    { id: 10, name: "Front Left Fender", remarks: "" },
    { id: 11, name: "Front Right Fender", remarks: "" },
    { id: 12, name: "Rear Left Fender", remarks: "" },
    { id: 13, name: "Rear Right Fender", remarks: "" },
    { id: 14, name: "Headlights", remarks: "" },
    { id: 15, name: "Taillights", remarks: "" },
    { id: 16, name: "Fog Lights", remarks: "" },
    { id: 17, name: "Side Mirrors", remarks: "" },
    { id: 18, name: "Spoiler", remarks: "" },
    { id: 19, name: "Alloy Rims", remarks: "" },
    { id: 20, name: "Spare Tyres / Tyre Gel Kit", remarks: "" },
    { id: 21, name: "Jack & Lever", remarks: "" },
    { id: 22, name: "Seat Condition", remarks: "" },
    { id: 23, name: "Hardtop/Sunroof/Moonroof", remarks: "" },
    { id: 24, name: "Power Windows (All Doors)", remarks: "" },
    { id: 25, name: "Power Mirrors (Adjustment/Folding)", remarks: "" },
    { id: 26, name: "Floor Mat", remarks: "" },
    { id: 27, name: "Back Camera / 4 Cameras", remarks: "" },
    { id: 28, name: "Warning Lights", remarks: "" },
    { id: 29, name: "Horn", remarks: "" },
    { id: 30, name: "Battery Condition", remarks: "" },
    { id: 31, name: "Suspension / Absorbers", remarks: "" },
    { id: 32, name: "Engine Noise / Vibration", remarks: "" },
    { id: 33, name: "Room Light", remarks: "" },
    { id: 34, name: "Back Wiper", remarks: "" },
    { id: 35, name: "Wiper Cover", remarks: "" },
    { id: 36, name: "Back reflector", remarks: "" },
    { id: 37, name: "Seat Cover", remarks: "" },
    { id: 38, name: "Door Panels (All Doors)", remarks: "" },
    { id: 39, name: "Parking Sensors", remarks: "" },
    { id: 40, name: "Side Body Kit", remarks: "" },
    { id: 41, name: "Steering Switch", remarks: "" },
    { id: 42, name: "Rear View Camera", remarks: "" },
    { id: 43, name: "Logo (Interior/exterior)", remarks: "" },
    { id: 44, name: "Door Speaker", remarks: "" },
  ]

  // -------------------- Validation --------------------
  const validationSchema = Yup.object({
    // Sender Information
    senderName: Yup.string().required("Please enter sender name"),
    senderMobile: Yup.string().required("Please enter sender mobile"),
    senderDesignation: Yup.string(),
    senderCompany: Yup.string(),

    // Receiver Information
    receiverName: Yup.string().required("Please enter receiver name"),
    receiverMobile: Yup.string().required("Please enter receiver mobile"),
    receiverDesignation: Yup.string(),
    receiverCompany: Yup.string(),

    // Vehicle Information
    vehicleName: Yup.string().required("Please enter vehicle name"),
    vehicleColor: Yup.string(),
    vehicleModelYear: Yup.string(),
    tyreSize: Yup.string(),
    chassisNo: Yup.string().required("Please enter chassis number"),
    engineNo: Yup.string().required("Please enter engine number"),
    fuelType: Yup.string(),
    cc: Yup.string(),
    km: Yup.string(),
  })

  // -------------------- Formik --------------------
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      // Sender Information
      senderName: editedData?.senderName || "",
      senderMobile: editedData?.senderMobile || "",
      senderDesignation: editedData?.senderDesignation || "",
      senderCompany: editedData?.senderCompany || "",

      // Receiver Information
      receiverName: editedData?.receiverName || "",
      receiverMobile: editedData?.receiverMobile || "",
      receiverDesignation: editedData?.receiverDesignation || "",
      receiverCompany: editedData?.receiverCompany || "",

      // Vehicle Information
      vehicleName: editedData?.vehicleName || "",
      vehicleColor: editedData?.vehicleColor || "",
      vehicleModelYear: editedData?.vehicleModelYear || "",
      tyreSize: editedData?.tyreSize || "",
      chassisNo: editedData?.chassisNo || "",
      engineNo: editedData?.engineNo || "",
      fuelType: editedData?.fuelType || "",
      cc: editedData?.cc || "",
      km: editedData?.km || "",

      // Accessories - initialize with empty remarks
      accessories: accessoriesList.reduce((acc, item) => {
        acc[item.id] = editedData?.accessories?.[item.id] || ""
        return acc
      }, {}),
    },
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          ...values,
        }

        const action = isEdit
          ? editMutation({ id: editedData.id, ...payload })
          : createMutation(payload)

        const res = await action
        toast.success(
          res?.message ??
            `Inventory ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
      } catch (error) {
        console.error(error)
        toast.error("Failed to save inventory.")
      }
    },
  })

  const buttonText = useMemo(() => {
    const loading = editLoading || createLoading
    return `${isEdit ? "Save Changes" : "Add Inventory"}${loading ? "..." : ""}`
  }, [isEdit, editLoading, createLoading])

  // Handle accessories remarks change
  const handleAccessoryRemarkChange = (id, value) => {
    formik.setFieldValue(`accessories.${id}`, value)
  }

  // -------------------- Render --------------------
  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Inventory" : "Add Inventory"}
      size="xl"
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        {/* Sender Information */}
        <CompanyStaffSelector
          formik={formik}
          companyField="senderCompany"
          staffFieldPrefix="sender"
          labelPrefix="Sender"
        />

        {/* Receiver Information */}
        <div className="mb-4">
          <h5>Receiver Information</h5>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label htmlFor="receiverName">
                  Name <span className="text-danger">*</span>
                </Label>
                <InputField
                  type="text"
                  name="receiverName"
                  id="receiverName"
                  value={formik.values.receiverName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.receiverName && !!formik.errors.receiverName
                  }
                />
                {formik.touched.receiverName && formik.errors.receiverName && (
                  <FormFeedback type="invalid">
                    {formik.errors.receiverName}
                  </FormFeedback>
                )}
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label htmlFor="receiverMobile">
                  Mobile <span className="text-danger">*</span>
                </Label>
                <InputField
                  type="text"
                  name="receiverMobile"
                  id="receiverMobile"
                  value={formik.values.receiverMobile}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.receiverMobile &&
                    !!formik.errors.receiverMobile
                  }
                />
                {formik.touched.receiverMobile &&
                  formik.errors.receiverMobile && (
                    <FormFeedback type="invalid">
                      {formik.errors.receiverMobile}
                    </FormFeedback>
                  )}
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label htmlFor="receiverDesignation">Designation</Label>
                <InputField
                  type="text"
                  name="receiverDesignation"
                  id="receiverDesignation"
                  value={formik.values.receiverDesignation}
                  onChange={formik.handleChange}
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label htmlFor="receiverCompany">Company</Label>
                <InputField
                  type="text"
                  name="receiverCompany"
                  id="receiverCompany"
                  value={formik.values.receiverCompany}
                  onChange={formik.handleChange}
                />
              </div>
            </Col>
          </Row>
        </div>

        {/* Vehicle Information */}
        <div className="mb-4">
          <h5>Vehicle Information</h5>
          <Row>
            <Col md={4}>
              <div className="mb-3">
                <Label htmlFor="chassisNo">
                  Chassis No. <span className="text-danger">*</span>
                </Label>
                <InputField
                  type="text"
                  name="chassisNo"
                  id="chassisNo"
                  value={formik.values.chassisNo}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.chassisNo && !!formik.errors.chassisNo
                  }
                />
                {formik.touched.chassisNo && formik.errors.chassisNo && (
                  <FormFeedback type="invalid">
                    {formik.errors.chassisNo}
                  </FormFeedback>
                )}
              </div>
            </Col>
            <Col md={4}>
              <div className="mb-3">
                <Label htmlFor="vehicleName">
                  Vehicle Name <span className="text-danger">*</span>
                </Label>
                <InputField
                  type="text"
                  name="vehicleName"
                  id="vehicleName"
                  value={formik.values.vehicleName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.vehicleName && !!formik.errors.vehicleName
                  }
                />
                {formik.touched.vehicleName && formik.errors.vehicleName && (
                  <FormFeedback type="invalid">
                    {formik.errors.vehicleName}
                  </FormFeedback>
                )}
              </div>
            </Col>
            <Col md={4}>
              <div className="mb-3">
                <Label htmlFor="vehicleColor">Color</Label>
                <InputField
                  type="text"
                  name="vehicleColor"
                  id="vehicleColor"
                  value={formik.values.vehicleColor}
                  onChange={formik.handleChange}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <div className="mb-3">
                <Label htmlFor="vehicleModelYear">Model/Year</Label>
                <InputField
                  type="text"
                  name="vehicleModelYear"
                  id="vehicleModelYear"
                  value={formik.values.vehicleModelYear}
                  onChange={formik.handleChange}
                />
              </div>
            </Col>

            <Col md={4}>
              <div className="mb-3">
                <Label htmlFor="tyreSize">Tyre Size</Label>
                <InputField
                  type="text"
                  name="tyreSize"
                  id="tyreSize"
                  value={formik.values.tyreSize}
                  onChange={formik.handleChange}
                />
              </div>
            </Col>
            <Col md={4}>
              <div className="mb-3">
                <Label htmlFor="engineNo">
                  Engine No. <span className="text-danger">*</span>
                </Label>
                <InputField
                  type="text"
                  name="engineNo"
                  id="engineNo"
                  value={formik.values.engineNo}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.engineNo && !!formik.errors.engineNo}
                />
                {formik.touched.engineNo && formik.errors.engineNo && (
                  <FormFeedback type="invalid">
                    {formik.errors.engineNo}
                  </FormFeedback>
                )}
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <div className="mb-3">
                <Label htmlFor="fuelType">Fuel Type</Label>
                <InputField
                  type="text"
                  name="fuelType"
                  id="fuelType"
                  value={formik.values.fuelType}
                  onChange={formik.handleChange}
                />
              </div>
            </Col>
            <Col md={4}>
              <div className="mb-3">
                <Label htmlFor="cc">CC:</Label>
                <InputField
                  type="text"
                  name="cc"
                  id="cc"
                  value={formik.values.cc}
                  onChange={formik.handleChange}
                />
              </div>
            </Col>
            <Col md={4}>
              <div className="mb-3">
                <Label htmlFor="km">KM</Label>
                <InputField
                  type="text"
                  name="km"
                  id="km"
                  value={formik.values.km}
                  onChange={formik.handleChange}
                />
              </div>
            </Col>
          </Row>
        </div>

        {/* Accessories */}
        <div className="mb-4">
          <h5>Accessories</h5>
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            <Table bordered responsive>
              <thead>
                <tr>
                  <th width="10%">SL</th>
                  <th width="40%">Items Name</th>
                  <th width="50%">Remarks</th>
                </tr>
              </thead>
              <tbody>
                {accessoriesList.map((item, index) => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <td>{item.name}</td>
                    <td>
                      <InputField
                        type="text"
                        value={formik.values.accessories[item.id] || ""}
                        onChange={e =>
                          handleAccessoryRemarkChange(item.id, e.target.value)
                        }
                        placeholder="Enter remarks"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>

        {/* Submit */}
        <div className="text-end mt-3">
          <Button
            color="success"
            type="submit"
            disabled={editLoading || createLoading}
          >
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default InventoryAddEdit
